import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Loader2, Sparkles, Play, StopCircle, RefreshCw, Zap } from "lucide-react";

// Admin-only "Pre-warm memorial cards" widget.
//
// Backend endpoints:
//   POST /api/admin/memorial-cards/prewarm/start?limit=N
//   POST /api/admin/memorial-cards/prewarm/cancel
//   GET  /api/admin/memorial-cards/prewarm/status
//
// The batch runs in the app event loop as a background task; this
// widget polls status every 2 s while a run is active so the admin
// can watch progress live and cancel mid-run.
export default function MemorialCardPrewarmPanel() {
  const [status, setStatus] = useState(null);
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await http.get("/admin/memorial-cards/prewarm/status");
      setStatus(data);
    } catch (e) { /* stay quiet — admin sees the last snapshot */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const params = limit ? { limit: Number(limit) } : {};
      await http.post("/admin/memorial-cards/prewarm/start", null, { params });
      toast.success("Pre-warm started.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start pre-warm.");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await http.post("/admin/memorial-cards/prewarm/cancel");
      toast.success("Cancel signalled — the current card will finish then stop.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel.");
    } finally { setBusy(false); }
  };

  const state = status?.state || "idle";
  const pct = status && status.total ? Math.round((status.done / status.total) * 100) : 0;
  const running = state === "running";
  const pending = status?.pending ?? "…";

  return (
    <section
      className="wofy-card mb-8"
      data-testid="prewarm-panel"
      style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #ECFDF5 100%)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4" style={{ color: "#B45309" }} />
        <div className="overline" style={{ color: "#B45309" }}>MEMORIAL CARD PRE-WARM</div>
      </div>
      <h3 className="font-display text-2xl">Generate cards for every imported obituary</h3>
      <p className="mt-1 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Fires the Nano-Banana pipeline for each imported memorial that still
        has no memorial card. Skips rows already generated. Safe to re-run.
        Currently pending: <b>{pending}</b>.
      </p>

      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="prewarm-controls">
        <label className="text-xs font-mono uppercase tracking-widest opacity-70">
          Limit (optional)
        </label>
        <input
          type="number"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          data-testid="prewarm-limit"
          disabled={running}
          placeholder="all"
          min={1}
          max={1000}
          className="w-24 rounded-full border px-3 py-1 text-xs bg-white/60"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <button
          type="button"
          onClick={start}
          disabled={busy || running}
          data-testid="prewarm-start"
          className="wofy-btn-primary inline-flex items-center gap-1.5 text-sm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? "Running…" : "Start pre-warm"}
        </button>
        {running && (
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            data-testid="prewarm-cancel"
            className="wofy-btn-ghost inline-flex items-center gap-1.5 text-sm text-red-700"
          >
            <StopCircle className="w-4 h-4" /> Cancel
          </button>
        )}
        <button
          type="button"
          onClick={refresh}
          data-testid="prewarm-refresh"
          className="wofy-btn-ghost inline-flex items-center gap-1.5 text-xs"
        >
          <RefreshCw className="w-3 h-3" /> refresh
        </button>
      </div>

      {status && (status.total > 0 || state !== "idle") && (
        <div className="mt-5" data-testid="prewarm-progress">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-mono uppercase tracking-widest opacity-70">
              {stateLabel(state)}
            </span>
            <span className="tabular-nums">
              {status.done} / {status.total} · {status.ok} ok · {status.failed} failed
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "#E5E7EB" }}>
            <div
              className="h-full transition-all"
              style={{
                width: `${pct}%`,
                background: state === "done" ? "#10B981" : state === "cancelled" ? "#F97316" : "#B45309",
              }}
            />
          </div>
          {status.last_error && (
            <div className="mt-2 text-[11px] text-red-700 font-mono truncate">
              last error: {status.last_error}
            </div>
          )}
          {status.recent?.length > 0 && (
            <div className="mt-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              <div className="font-mono uppercase tracking-widest opacity-70 mb-1">Recent</div>
              <ul className="space-y-0.5">
                {status.recent.slice(0, 5).map((r) => (
                  <li key={r.id} className="truncate flex items-center gap-1">
                    <Zap className="w-3 h-3 opacity-40" /> {r.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function stateLabel(s) {
  switch (s) {
    case "idle":       return "Idle";
    case "running":    return "Running";
    case "done":       return "Complete";
    case "cancelled":  return "Cancelled";
    case "cancelling": return "Cancelling…";
    case "error":      return "Error";
    default:           return s;
  }
}
